import React from "react"
import { observer } from "mobx-react-lite"
import { FieldProps } from "formik"
import without from "lodash/without"
import debounce from "lodash/debounce"

import Dropdown from "@components/prototypes/FilterSidebar/components/Dropdown/Dropdown"
import NotFound from "@components/ui/NotFound/NotFound"
import Loader from "@components/ui/Loader/BarLoader"
import Templates from "@components/ui/Templates"
import { useStore } from "@store/index"
import CheckboxWithLabel from "@components/ui/Checkbox/CheckboxWithLabel"
import ListItem from "@components/ui/ListItem/ListItem"
import DocumentIcon from "@components/ui/Icon/DocumentIcon"
import List from "@components/ui/List/List"
import InfiniteCollection from "@components/modals/EditModal/components/BaseContainer/InfiniteCollection"
import { SearchContext } from "@components/prototypes/SearchContext"
import { DataSourceNodeContextProps } from "@framework/constants/upload"

import styles from "./DataSourceSelect.module.sass"

const DEBOUNCE_TIMEOUT = 500

export interface DataSourceSelectProps extends FieldProps<string[]> {
  label?: string
}

const DataSourceSelect: React.FC<DataSourceSelectProps> = observer(
  ({ label, field, form }) => {
    const context = React.useContext(SearchContext)

    const [open, setOpen] = React.useState(false)

    const { value = [], name } = field
    const { setFieldValue } = form

    const { allDataSourcesStore } = useStore()

    const handleClick = (newValue: string) => {
      if (!value.includes(newValue)) {
        setFieldValue(name, [...value, newValue])
      } else {
        setFieldValue(name, without(value, newValue))
      }
    }

    const debouncedSearch = React.useCallback(
      debounce(allDataSourcesStore.search, DEBOUNCE_TIMEOUT),
      []
    )

    React.useEffect(() => {
      debouncedSearch(context?.query ?? "")
    }, [context?.query])

    const renderRow = (item: DataSourceNodeContextProps) => {
      const active = value?.includes(item.name as string)

      return (
        <ListItem
          active={active}
          onClick={() => handleClick(item.name as string)}
        >
          <List direction="row" gutter="16" align="center">
            <DocumentIcon icon={item.icon ?? "global"} size="medium" />
            {item.displayName}
          </List>
        </ListItem>
      )
    }

    const isChecked = !!value?.length

    const handleUncheck = (e: React.MouseEvent) => {
      if (isChecked) setFieldValue(name, undefined)
      else setOpen(true)
      e.stopPropagation()
    }

    return (
      <Dropdown
        title={label}
        opened={open}
        onClick={() => setOpen((value) => !value)}
        before={
          <CheckboxWithLabel
            checked={!!value?.length}
            onClick={handleUncheck}
          />
        }
      >
        <Templates.RollScript className={styles.root}>
          <div className={styles.container}>
            {allDataSourcesStore.state.total === 0 ? (
              allDataSourcesStore.state.isLoading ? (
                <Loader size="large" fluid />
              ) : (
                <NotFound>No Content Source found</NotFound>
              )
            ) : (
              <InfiniteCollection<DataSourceNodeContextProps>
                total={allDataSourcesStore.state.total}
                getItem={allDataSourcesStore.getByIndex}
                renderItem={renderRow}
                minRowHeight={42}
              />
            )}
          </div>
        </Templates.RollScript>
      </Dropdown>
    )
  }
)

export default DataSourceSelect
